import { formatDistanceToNow } from 'date-fns'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useAudiences } from '@/api/hooks/use-audiences'

interface Props {
  onAudienceChange?: (audienceId: string) => void
}

export function SelectAudienceInline({ onAudienceChange }: Props): React.JSX.Element {
  const { data: audiencesData, isLoading } = useAudiences()
  const audiences = audiencesData?.data ?? []
  const [selectedId, setSelectedId] = useState('')

  const hasMultiple = audiences.length > 1
  // Only auto-select if there's exactly 1 audience
  const effectiveId = selectedId || (audiences.length === 1 ? (audiences[0]?.id ?? '') : '')
  const selectedAudience = audiences.find((a) => a.id === effectiveId)

  // Notify parent when audience changes
  useEffect(() => {
    onAudienceChange?.(effectiveId)
  }, [effectiveId, onAudienceChange])

  function handleChange(id: string): void {
    setSelectedId(id)
  }

  return (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="wp-audience-inline"
          className="block text-sm font-medium text-text-secondary"
        >
          Audience
        </label>
        {hasMultiple && !selectedId && (
          <p className="mt-1 text-xs text-amber-600 font-medium">
            You have {audiences.length} audiences — please select which one should receive
            subscribers.
          </p>
        )}
        <select
          id="wp-audience-inline"
          value={effectiveId}
          onChange={(e) => handleChange(e.target.value)}
          disabled={isLoading}
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
        >
          {isLoading && <option value="">Loading audiences...</option>}
          {!isLoading && audiences.length === 0 && <option value="">No audiences found</option>}
          {!isLoading && hasMultiple && !selectedId && (
            <option value="">— Select an audience —</option>
          )}
          {audiences.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.subscriber_count.toLocaleString()} subscribers)
            </option>
          ))}
        </select>
      </div>

      {selectedAudience && (
        <div className="flex items-center gap-6 rounded-lg bg-surface-1 px-4 py-3">
          <div>
            <p className="text-xs text-text-tertiary">Subscribers</p>
            <p className="text-sm font-medium text-text-primary">
              {selectedAudience.subscriber_count.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-tertiary">Created</p>
            <p className="text-sm font-medium text-text-primary">
              {formatDistanceToNow(new Date(selectedAudience.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
      )}

      {!isLoading && audiences.length === 0 && (
        <Link
          to="/audiences"
          className="inline-flex items-center gap-1.5 text-sm text-brand-500 hover:text-brand-600"
        >
          <Plus className="h-4 w-4" />
          Create New Audience
        </Link>
      )}
    </div>
  )
}
